'use client';

import { X } from 'lucide-react';
import type { ReleaseHighlight } from '@/actions/releases';

interface WhatsNewModalProps {
  headline: string | null;
  intro: string | null;
  summary: string;
  highlights: ReleaseHighlight[];
  onClose: () => void;
  /** Omit to hide the "nerdy stuff" link — used pre-login, where /changelog would just bounce back to the login form. */
  changelogHref?: string;
}

/** Layer 1 of the two-layer update system: the short, user-facing "what changed and why you should care" view. */
export default function WhatsNewModal({ headline, intro, summary, highlights, onClose, changelogHref }: WhatsNewModalProps) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="What's new"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative w-full max-w-md rounded-2xl shadow-xl bg-[var(--bg-surface)] border border-[var(--border)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-4 border-b border-[var(--border)]">
          <div className="flex items-start justify-between gap-3">
            <h2 className="text-lg font-bold text-[var(--text-1)] leading-snug">{headline || "What's New"}</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="text-[var(--text-3)] hover:text-[var(--text-1)] -mt-1 -mr-1 p-1 flex-shrink-0"
            >
              <X size={18} />
            </button>
          </div>
          <p className="mt-2 text-sm text-[var(--text-2)] leading-relaxed">{intro || summary}</p>
        </div>

        {highlights.length > 0 && (
          <ul className="px-6 py-4 space-y-3 max-h-[50vh] overflow-y-auto">
            {highlights.map((h, i) => (
              <li key={i} className="flex gap-3">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--amber)' }} aria-hidden="true" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[var(--text-1)]">{h.title}</div>
                  {h.body && <div className="text-xs text-[var(--text-2)] mt-0.5">{h.body}</div>}
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="px-6 py-4 bg-[var(--bg-raised)] flex items-center justify-between gap-3">
          {changelogHref ? (
            <a href={changelogHref} className="text-xs font-medium hover:underline" style={{ color: 'var(--amber)' }}>
              Want the nerdy stuff? See the full update →
            </a>
          ) : <span />}
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 px-4 py-2 rounded-lg text-sm font-bold"
            style={{ backgroundColor: 'var(--amber)', color: 'var(--primary-foreground)' }}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
